import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  QrCode, LayoutDashboard, Plus, List, FolderTree, ChevronDown, ArrowLeft, Home, LogOut, User, Bell,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';
import { Toaster } from '../../components/ui/toaster';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';

import QrBuilderDashboard from './qr-builder/QrBuilderDashboard';
import QrCodesList from './qr-builder/QrCodesList';
import QrCodeCreate from './qr-builder/QrCodeCreate';
import QrProjects from './qr-builder/QrProjects';

const menuSections = [
  {
    id: 'overview',
    label: 'Overview',
    children: [
      { path: '/admin/qr-builder', label: 'Dashboard', icon: LayoutDashboard, description: 'Your QR codes at a glance' },
    ],
  },
  {
    id: 'codes',
    label: 'QR Codes',
    children: [
      { path: '/admin/qr-builder/codes', label: 'All QR Codes', icon: List, description: 'View, edit & download' },
      { path: '/admin/qr-builder/new', label: 'Create New', icon: Plus, description: 'Build a new QR code' },
      { path: '/admin/qr-builder/projects', label: 'Projects', icon: FolderTree, description: 'Group & color-code' },
    ],
  },
];

const QrBuilderLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, loading } = useAuth();
  const { logoUrl, siteName } = useSiteSettings();
  const flags = useSiteFeatureFlags();
  const [expandedSections, setExpandedSections] = useState(['overview', 'codes']);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId]
    );
  };

  const isActive = (path) => {
    if (path === '/admin/qr-builder') return location.pathname === '/admin/qr-builder';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/admin/qr-builder/codes') return <QrCodesList />;
    if (path === '/admin/qr-builder/new') return <QrCodeCreate />;
    if (path === '/admin/qr-builder/projects') return <QrProjects />;
    if (path.startsWith('/admin/qr-builder/edit/')) {
      const id = path.split('/admin/qr-builder/edit/')[1];
      return <QrCodeCreate editId={id} />;
    }
    return <QrBuilderDashboard />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF6B00] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Follows the "QR Codes" flag: when it is off this section behaves as if it does not exist.
  // (The API independently answers 404, so this is presentation, not access control.)
  if (!flags._loaded) return null;
  if (!flags.portal_qr_codes_enabled) return <Navigate to="/admin/cart" replace />;

  // Full QR Builder access is a perk of having any account, not just staff -
  // anyone who registers gets it, unlike the rest of /admin which stays
  // staff/admin only.

  return (
    <div className="min-h-screen bg-gray-50 flex" data-theme="light" data-testid="qr-builder-layout">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r border-gray-200 flex flex-col">
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 bg-gradient-to-r from-[#1a1c1b] to-[#302f2c]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#FF6B00]/20 rounded-lg">
              <QrCode className="w-5 h-5 text-[#FF6B00]" />
            </div>
            <span className="font-bold text-white">QR BUILDER</span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          {menuSections.map((section) => (
            <div key={section.id} className="mb-4">
              <button
                onClick={() => toggleSection(section.id)}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg transition-all ${
                  expandedSections.includes(section.id)
                    ? 'bg-[#FF6B00] text-white'
                    : 'text-gray-500 hover:bg-orange-50'
                }`}
              >
                {section.label}
                <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.includes(section.id) ? '' : '-rotate-90'}`} />
              </button>

              {expandedSections.includes(section.id) && (
                <div className="mt-2 space-y-1 ml-2 border-l-2 pl-2 border-[#FF6B00]/40">
                  {section.children.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-start gap-3 px-3 py-3 rounded-lg transition-all ${
                        isActive(item.path) ? 'bg-[#FF6B00] text-white' : 'text-gray-700 hover:bg-orange-50'
                      }`}
                    >
                      <item.icon className={`w-5 h-5 mt-0.5 ${isActive(item.path) ? 'text-white' : 'text-[#FF6B00]'}`} />
                      <div>
                        <p className="font-medium text-sm">{item.label}</p>
                        <p className={`text-xs ${isActive(item.path) ? 'text-white/70' : 'text-gray-500'}`}>
                          {item.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <Link
            to="/admin"
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">Back to Admin</span>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img src={logoUrl} alt={siteName || 'Site logo'} className="h-8" />
            ) : (
              <span className="font-semibold text-gray-900">{siteName || 'A2G Office'}</span>
            )}
            <span className="text-gray-400">|</span>
            <p className="text-gray-500 text-sm">Create & manage QR codes</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="p-2 hover:bg-gray-100 rounded-lg" title="View Store">
              <Home className="w-5 h-5 text-gray-600" />
            </Link>
            <button className="p-2 hover:bg-gray-100 rounded-lg relative">
              <Bell className="w-5 h-5 text-gray-600" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-lg">
                  <div className="w-8 h-8 bg-[#FF6B00] rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="font-medium text-sm">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <DropdownMenuItem asChild>
                  <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                    <LayoutDashboard className="w-4 h-4" />
                    Admin Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/profile" className="flex items-center gap-2 cursor-pointer">
                    <User className="w-4 h-4" />
                    Edit Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="text-red-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6 bg-gray-50">
          {getCurrentPage()}
        </main>
      </div>

      <Toaster />
    </div>
  );
};

export default QrBuilderLayout;
